import { useEffect, useMemo, useState } from 'react';
import { TIMEZONES, formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

export function SystemTab({ currentUser, onSettingsChanged }) {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [siteName, setSiteName] = useState('');
  const [defaultTz, setDefaultTz] = useState('UTC');
  const [maintenance, setMaintenance] = useState('');

  // Load both system settings and the read-only server info in parallel.
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.settings.system(), api.settings.info()])
      .then(([s, i]) => {
        if (cancelled) return;
        setSettings(s.settings);
        setInfo(i);
        setSiteName(s.settings.site_name);
        setDefaultTz(s.settings.default_timezone);
        setMaintenance(s.settings.maintenance_message || '');
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError ? err.payload?.error || err.message : 'Failed to load settings.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return (
      siteName !== settings.site_name ||
      defaultTz !== settings.default_timezone ||
      (maintenance || '') !== (settings.maintenance_message || '')
    );
  }, [settings, siteName, defaultTz, maintenance]);

  const errors = useMemo(() => {
    const e = {};
    if (siteName.trim().length === 0 || siteName.length > 64) e.site_name = 'Site name must be 1-64 chars.';
    if (maintenance.length > 512) e.maintenance_message = 'Maintenance message must be at most 512 chars.';
    return e;
  }, [siteName, maintenance]);

  const canSave = dirty && Object.keys(errors).length === 0 && !busy;

  async function handleSave() {
    if (!canSave) return;
    setBusy(true);
    try {
      const res = await api.settings.updateSystem({
        site_name: siteName.trim(),
        default_timezone: defaultTz,
        maintenance_message: maintenance,
      });
      setSettings(res.settings);
      onSettingsChanged?.();
      toast.success('System settings saved.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  function handleReset() {
    if (!settings) return;
    setSiteName(settings.site_name);
    setDefaultTz(settings.default_timezone);
    setMaintenance(settings.maintenance_message || '');
  }

  if (loadError) {
    return (
      <div role="alert" className="card border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
        {loadError}
      </div>
    );
  }

  if (!settings || !info) {
    return (
      <div className="card px-6 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
        Loading settings…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General */}
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-gears" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">General</h2>
              <p className="text-sm text-mist">System-wide preferences applied across the coordinator.</p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
          <div>
            <label htmlFor="site-name" className="field-label">Site name</label>
            <input
              id="site-name"
              className="field"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              maxLength={64}
              disabled={busy}
            />
            {errors.site_name && <p className="mt-1 text-xs text-rose-600">{errors.site_name}</p>}
            <p className="mt-1 text-xs text-mist">Shown in the browser tab and in the sidebar header.</p>
          </div>

          <div>
            <label htmlFor="default-tz" className="field-label">Default timezone for new users</label>
            <select
              id="default-tz"
              className="field"
              value={defaultTz}
              onChange={(e) => setDefaultTz(e.target.value)}
              disabled={busy}
            >
              {TIMEZONES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label htmlFor="maintenance-msg" className="field-label">
              Maintenance message <span className="font-normal text-mist">(public — visible on login page)</span>
            </label>
            <textarea
              id="maintenance-msg"
              className="field min-h-[100px] resize-y"
              value={maintenance}
              onChange={(e) => setMaintenance(e.target.value)}
              maxLength={512}
              placeholder="Leave empty to hide the banner."
              disabled={busy}
            />
            <div className="mt-1 flex items-center justify-between">
              {errors.maintenance_message ? (
                <p className="text-xs text-rose-600">{errors.maintenance_message}</p>
              ) : (
                <p className="text-xs text-mist">Banner appears at the top of every page when non-empty.</p>
              )}
              <p className="text-xs text-mist">{maintenance.length}/512</p>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-canvas px-6 py-4">
          <button type="button" onClick={handleReset} disabled={!dirty || busy} className="btn-secondary">
            Reset
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save changes
              </>
            )}
          </button>
        </footer>
      </div>

      {/* Server info */}
      <ServerInfoCard info={info} viewerTimezone={currentUser.timezone} />
    </div>
  );
}

function ServerInfoCard({ info, viewerTimezone }) {
  const [serverTime, setServerTime] = useState(info.serverTime);
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      const next = await api.settings.info();
      setServerTime(next.serverTime);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="card">
      <header className="flex items-center justify-between border-b border-line px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className="fa-solid fa-server" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-ink">Server</h2>
            <p className="text-sm text-mist">Read-only build and runtime information.</p>
          </div>
        </div>
        <button type="button" onClick={refresh} disabled={refreshing} className="btn-secondary text-xs">
          <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          Refresh
        </button>
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-6 sm:grid-cols-2">
        <Pair label="Build version" value={info.build.version} mono />
        <Pair label="Built at" value={formatUtcInTimezone(info.build.builtAt, viewerTimezone)} />
        <Pair label="Server time" value={formatUtcInTimezone(serverTime, viewerTimezone)} />
        <Pair label="Server time (UTC)" value={formatUtcInTimezone(serverTime, 'UTC')} mono />
      </dl>
    </div>
  );
}

function Pair({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</dt>
      <dd className={`mt-1 text-sm text-ink ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
